import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Stack,
  TextField,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  alpha,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Backdrop,
  CircularProgress,
  InputAdornment,
  Tooltip,
  useTheme,
  Autocomplete,
  Fade,
  Zoom
} from '@mui/material';
import {
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  CalendarToday,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  Share as ShareIcon,
  ArrowUpward as ArrowUpwardIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
  LocalAtm as LocalAtmIcon,
  AccountBalance as AccountBalanceIcon,
  Paid as PaidIcon,
  Discount as DiscountIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { DataContext } from '../../context/DataContext';
import {
  generateTransactionBillHTML,
  getBillStyles
} from '../../components/billPrintUtils';
import '../../styles/ag-grid-premium-theme.css'

ModuleRegistry.registerModules([AllCommunityModule]);

// Custom Hooks
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Custom Cell Renderers
const StatusRenderer = (props) => {
  const isCancelled = (transaction) => {
    return (
      transaction?.transactionDetails?.type === "cancelled" ||
      transaction?.status === "cancelled" ||
      transaction?.isCancelled === true ||
      transaction?.transactionDetails?.isCancelled === true ||
      transaction?.cancelledAt !== undefined
    );
  };

  const cancelled = isCancelled(props.data);

  if (cancelled) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Chip
          label="CANCELLED"
          size="small"
          color="error"
          variant="filled"
          sx={{
            fontWeight: 700,
            borderRadius: '8px',
            minWidth: '100px',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(220, 38, 38, 0.15)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            textDecoration: 'line-through',
            '& .MuiChip-label': {
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }
          }}
          icon={<CancelIcon sx={{ fontSize: '14px', color: '#DC2626' }} />}
        />
      </Box>
    );
  }

  const pendingAmount = parseInt(parseFloat(props.data?.summary?.pendingAmount).toFixed(2)) || 0;
  const isPaid = pendingAmount === 0;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Chip
        label={isPaid ? 'Paid' : 'Pending'}
        size="small"
        color={isPaid ? 'success' : 'warning'}
        variant="filled"
        sx={{
          fontWeight: 600,
          borderRadius: '8px',
          minWidth: '80px',
          backdropFilter: 'blur(10px)',
          backgroundColor: isPaid
            ? 'rgba(16, 185, 129, 0.15)'
            : 'rgba(245, 158, 11, 0.15)',
          border: isPaid
            ? '1px solid rgba(16, 185, 129, 0.3)'
            : '1px solid rgba(245, 158, 11, 0.3)',
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }
        }}
        icon={
          isPaid ? (
            <CheckCircleIcon sx={{ fontSize: '14px', color: '#10B981' }} />
          ) : (
            <PendingIcon sx={{ fontSize: '14px', color: '#F59E0B' }} />
          )
        }
      />
    </Box>
  );
};

const AmountRenderer = (props) => {
  // const theme = useTheme();
  // const isDark = theme.palette.mode === 'dark';
  const value = props.value || 0;

  return (
    <Box sx={{
      textAlign: 'right',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
    }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          background: 'linear-gradient(135deg, #2563EB, #3B82F6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 0,
        }).format(value)}
      </Typography>
      {props.data?.summary?.pendingAmount > 0 && props.field === 'summary.grandTotal' && (
        <Typography
          variant="caption"
          sx={{
            color: '#EF4444',
            fontSize: '0.7rem',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            mt: '2px',
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: '10px' }} />
          {(props.data.summary.pendingAmount / value * 100).toFixed(1)}% due
        </Typography>
      )}
    </Box>
  );
};

const CustomerNameRenderer = (props) => {
  const { customers } = props.context;
  const customer = customers.find(c => c.id === props.value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 600,
        fontSize: '0.875rem',
        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
      }}>
        {customer?.name?.charAt(0) || 'C'}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {customer?.name || 'N/A'}
        </Typography>
        {customer?.contact && (
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            fontSize: '0.7rem',
          }}>
            {customer.contact}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ItemsRenderer = (props) => {
  const itemsCount = props.value?.length || 0;
  // const theme = useTheme();
  // const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
      <Chip
        label={`${itemsCount} item${itemsCount !== 1 ? 's' : ''}`}
        size="small"
        variant="filled"
        sx={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(167, 139, 250, 0.15))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          color: '#8B5CF6',
          fontWeight: 600,
          borderRadius: '8px',
          minWidth: '70px',
          '&:hover': {
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(167, 139, 250, 0.25))',
          },
        }}
        icon={<InventoryIcon sx={{ fontSize: '14px', color: '#8B5CF6' }} />}
      />
    </Box>
  );
};

// Make sure this is at the top level, outside the component
const isMobileDevice = () => {
  // Check both user agent and screen size
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isSmallScreen = window.innerWidth < 768;
  return isMobileUA || isSmallScreen;
};

const TransactionHistory = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { customerTransactions, customers, loading: contextLoading, refresh, removeCustomerTransactionAndUpdateStock, stock } = useContext(DataContext);
  console.log(customerTransactions)
  // Refs
  const gridRef = useRef();
  // const billContentRef = useRef();

  // States
  const isPageLoading = false;
  const isSearching = false;
  const isFiltering = false;
  const [isExporting, setIsExporting] = useState(false);
  // const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [silverItemsArray, setSilverItemsArray] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null
  });
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isSharing, setIsSharing] = useState(false);

  // Debounced search
  const searchTerm = useDebounce(searchInput, 300);

  // Initialize default dates (current financial year)
  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    let startDate, endDate;

    if (currentMonth >= 3) {
      startDate = new Date(currentYear, 3, 1);
      endDate = today;
    } else {
      startDate = new Date(currentYear - 1, 3, 1);
      endDate = today;
    }

    setDates({ startDate, endDate });
  }, []);


  // Apply all filters
  const applyFilters = useCallback(() => {
    if (!customerTransactions || customerTransactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }

    let filtered = [...customerTransactions];

    // 1. Filter by customer
    if (customerFilter !== 'all') {
      filtered = filtered.filter(transaction =>
        transaction.customer === customerFilter
      );
    }

    // 2. Filter by date range
    if (dates.startDate && dates.endDate) {
      const startDateStr = format(dates.startDate, 'dd/MM/yyyy');
      const endDateStr = format(dates.endDate, 'dd/MM/yyyy');
      filtered = filterByDateRange(filtered, startDateStr, endDateStr);
    }

    // 3. Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(transaction => {
        const customer = customers.find(c => c.id === transaction.customer);

        // Search in customer name
        const customerNameMatch = customer?.name?.toLowerCase().includes(term);

        // Search in customer phone
        // const customerPhoneMatch = customer?.contact?.toString().includes(term);

        // Search in bill number
        const billNoMatch = transaction.transactionDetails?.billNo?.toString().includes(term);

        // Search in items
        const itemsMatch = transaction.items?.some(item =>
          item.name?.toLowerCase().includes(term) ||
          item.huid?.toLowerCase().includes(term)
        );

        // Search in date
        const dateMatch = transaction.date?.toLowerCase().includes(term);

        return customerNameMatch || billNoMatch || itemsMatch || dateMatch;
      });
    }

    // 4. Sort by date (newest first)
    // 4. Sort by creation time (newest first) - using date and time
    filtered.sort((a, b) => {
      // If you have a createdAt field
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }

      // If you have date and time separately
      if (a.transactionTime && b.transactionTime) {
        const dateTimeA = new Date(`${a.date} ${a.transactionTime}`);
        const dateTimeB = new Date(`${b.date} ${b.transactionTime}`);
        return dateTimeB - dateTimeA;
      }

      // Fallback to just date
      const parseDate = (dateStr) => {
        try {
          return parse(dateStr, 'dd/MM/yyyy', new Date());
        } catch {
          return new Date(0);
        }
      };
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateB - dateA;
    });

    setFilteredTransactions(filtered);
  }, [customerTransactions, customerFilter, dates.startDate, dates.endDate, searchTerm, customers]);

  // Apply filters whenever any filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [customerTransactions, customerFilter, dates.startDate, dates.endDate, searchTerm, applyFilters]);

  // React version of the filterByDateRange function
  function filterByDateRange(records, startDate, endDate) {
    if (!records || records.length === 0) return [];
    if (!startDate || !endDate) return records;

    // Parse filter dates (from frontend - always DD/MM/YYYY)
    const parseFilterDate = (dateStr) => {
      if (!dateStr) return null;

      // If it's already a Date object
      if (dateStr instanceof Date) {
        return dateStr;
      }

      // Handle DD/MM/YYYY format
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        // Validate day, month, year
        if (isNaN(day) || isNaN(month) || isNaN(year)) {
          return null;
        }

        const date = new Date(year, month, day);

        // Validate the created date
        if (isNaN(date.getTime()) ||
          date.getDate() !== day ||
          date.getMonth() !== month ||
          date.getFullYear() !== year) {
          return null;
        }

        return date;
      }

      // Try ISO format
      const isoDate = new Date(dateStr);
      return isNaN(isoDate.getTime()) ? null : isoDate;
    };

    // Parse record dates - handle multiple formats
    const parseRecordDate = (dateStr) => {
      if (!dateStr) return null;

      // If it's already a Date object
      if (dateStr instanceof Date) {
        return dateStr;
      }

      // Handle string dates
      if (typeof dateStr === 'string') {
        // Try DD/MM/YYYY first (intended format)
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);

          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime()) &&
              date.getDate() === day &&
              date.getMonth() === month &&
              date.getFullYear() === year) {
              return date;
            }
          }
        }

        // Try MM/DD/YYYY (Google Sheets might have converted it)
        if (parts.length === 3) {
          const month = parseInt(parts[0], 10) - 1;
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);

          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime()) &&
              date.getDate() === day &&
              date.getMonth() === month &&
              date.getFullYear() === year) {
              return date;
            }
          }
        }

        // Try YYYY-MM-DD format
        if (dateStr.includes('-')) {
          const isoParts = dateStr.split('-');
          if (isoParts.length === 3) {
            const year = parseInt(isoParts[0], 10);
            const month = parseInt(isoParts[1], 10) - 1;
            const day = parseInt(isoParts[2], 10);

            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
              const date = new Date(year, month, day);
              if (!isNaN(date.getTime())) {
                return date;
              }
            }
          }
        }

        // Try ISO format (2025-11-01T18:30:00.000Z)
        const isoDate = new Date(dateStr);
        if (!isNaN(isoDate.getTime())) {
          return isoDate;
        }
      }

      return null;
    };

    const start = parseFilterDate(startDate);
    const end = parseFilterDate(endDate);

    if (!start || !end) {
      return records;
    }

    // Set end date to end of day for inclusive range
    const endOfDay = new Date(end);
    endOfDay.setHours(23, 59, 59, 999);

    return records.filter(record => {
      if (!record.date) return false;

      const recordDate = parseRecordDate(record.date);
      if (!recordDate) return false;

      return recordDate >= start && recordDate <= endOfDay;
    });
  }

  // Show Alert
  const showAlert = useCallback((message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(''), 3000);
  }, []);

  // Load Transactions
  // const loadTransactions = useCallback(async () => {
  //   if (dates.startDate && dates.endDate) {
  //     setIsFiltering(true);
  //     try {
  //       // Apply filters will automatically run due to useEffect
  //       showAlert('Transactions filtered successfully', 'success');
  //     } catch (error) {
  //       console.error('Error filtering transactions:', error);
  //       showAlert('Failed to filter transactions', 'error');
  //     } finally {
  //       setIsFiltering(false);
  //     }
  //   }
  // }, [dates, showAlert]);

  // Add this function after showAlert and before handleMobileShare
  const generatePDFBlob = useCallback(async (transaction) => {
    try {
      // Generate bill HTML
      const billHtml = generateTransactionBillHTML(transaction, customers);

      // Create temporary div
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = billHtml;
      tempDiv.style.width = '210mm';
      tempDiv.style.padding = '0';
      tempDiv.style.margin = '0';
      tempDiv.style.backgroundColor = '#ffffff';
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      document.body.appendChild(tempDiv);

      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 300));

      // Capture as image using html2canvas
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      // Remove temporary div
      document.body.removeChild(tempDiv);

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = imgWidth / imgHeight;

      let finalWidth, finalHeight;
      const margin = 10;
      const maxWidth = pageWidth - 2 * margin;
      const maxHeight = pageHeight - 2 * margin;

      if (imgWidth > imgHeight) {
        finalWidth = maxWidth;
        finalHeight = maxWidth / ratio;
        if (finalHeight > maxHeight) {
          finalHeight = maxHeight;
          finalWidth = maxHeight * ratio;
        }
      } else {
        finalHeight = maxHeight;
        finalWidth = maxHeight * ratio;
        if (finalWidth > maxWidth) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / ratio;
        }
      }

      const x = (pageWidth - finalWidth) / 2;
      const y = (pageHeight - finalHeight) / 2;

      pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);

      // Return as Blob
      return pdf.output('blob');
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }, [customers]);


  const handleMobileShare = useCallback(async (transaction) => {
    try {
      setIsSharing(true);
      showAlert('Generating PDF for sharing...', 'info');

      // Generate PDF as Blob
      const pdfBlob = await generatePDFBlob(transaction);

      // Create a File object from the Blob
      const fileName = `Bill_${transaction.transactionDetails?.billNo || 'N/A'}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Check if file sharing is supported
      if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        // Share the PDF file
        await navigator.share({
          files: [pdfFile],
          title: `Bill #${transaction.transactionDetails?.billNo}`,
        });
        showAlert('PDF shared successfully!', 'success');
      } else {
        // Fallback: Try sharing as text
        const customer = customers.find(c => c.id === transaction.customer);
        const shareData = {
          title: `Bill #${transaction.transactionDetails?.billNo}`,
          text: `Bill #${transaction.transactionDetails?.billNo}\nCustomer: ${customer?.name || 'N/A'}\nTotal: ₹${transaction.summary?.grandTotal?.toLocaleString('en-IN')}`,
        };
        await navigator.share(shareData);
        showAlert('Bill details shared!', 'success');
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        showAlert('Failed to share PDF. Please try again.', 'error');
      }
    } finally {
      setIsSharing(false);
    }
  }, [customers, showAlert, generatePDFBlob]);


  const handleDesktopPrint = useCallback((transaction) => {
    if (!transaction) return;

    const isGst = transaction.transactionDetails?.gstApplicable;
    const billHtml = generateTransactionBillHTML(transaction, customers);
    const styles = getBillStyles();

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the bill');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Bill - ${isGst ? 'GST Invoice' : 'Cash Memo'}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            ${styles}
            
            @media print {
              body {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              table {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              
              tr {
                page-break-inside: avoid !important;
                page-break-after: auto !important;
                break-inside: avoid !important;
              }
              
              thead {
                display: table-header-group !important;
              }
              
              tfoot {
                display: table-footer-group !important;
              }
            }
            
            .watermark {
              position: fixed !important;
              top: 50% !important;
              left: 50% !important;
              transform: translate(-50%, -50%) rotate(-45deg) !important;
              opacity: 0.1 !important;
              z-index: -1 !important;
              pointer-events: none !important;
            }
            
            .watermark img {
              width: 400px !important;
              height: 400px !important;
              object-fit: contain !important;
            }
          </style>
        </head>
        <body>
          ${billHtml}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  setTimeout(() => window.close(), 100);
                };
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }, [customers]);


  const handlePrintBill = useCallback((transaction) => {
    if (!transaction) return;

    console.log('handlePrintBill called with transaction:', transaction);
    console.log('isMobileDevice:', isMobileDevice());
    console.log('navigator.share available:', !!navigator.share);

    // Check if it's a mobile device with share capability
    if (isMobileDevice() && navigator.share) {
      console.log('Mobile share triggered');
      handleMobileShare(transaction);
      return;
    }

    console.log('Desktop print triggered');
    handleDesktopPrint(transaction);
  }, [handleMobileShare, handleDesktopPrint]); // Make sure both are in dependencies

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState(null);
  const [refundAmounts, setRefundAmounts] = useState({
    cash: 0,
    bank: 0,
    total: 0
  });
  const [cancellingItems, setCancellingItems] = useState([]);
  const [isCancelling, setIsCancelling] = useState(false);

  // Add this function to handle refund amount changes
  const handleRefundChange = (type, value) => {
    const numValue = parseFloat(value) || 0;
    setRefundAmounts(prev => {
      const updated = {
        ...prev,
        [type]: numValue
      };
      updated.total = updated.cash + updated.bank;
      return updated;
    });
  };

  // Add this function to reset refund amounts when modal closes
  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
    setSelectedCancelTransaction(null);
    setRefundAmounts({ cash: 0, bank: 0, total: 0 });
    setCancellingItems([]);
    setSilverItemsArray([]);
  };

  const handleCancelBill = useCallback((transaction) => {
    console.log('Cancel Bill:', transaction);

    // Parse jewelry name utility function
    const parseJewelryName = (name) => {
      if (!name || typeof name !== 'string') {
        return {
          type: '',
          serialNo: '',
          metal: '',
          purity: '',
          originalName: name || '',
          isValid: false
        };
      }

      const matchingItems = stock.filter(stockItem =>
        stockItem.qr === name &&
        (stockItem.sold === "Yes" || stockItem.sold === true)
      );

      return {
        type: matchingItems[0]?.name || '',
        serialNo: matchingItems[0]?.serialNo || '',
        metal: matchingItems[0]?.metal || '',
        purity: matchingItems[0]?.melting || '',
        originalName: matchingItems[0]?.name || '',
        isValid: !!matchingItems[0]?.name || !!matchingItems[0]?.serialNo
      };
    };

    const findFirstMissingNumber = (arr, startFrom = 1) => {
      if (arr.length === 0) return startFrom;
      const numberSet = new Set(arr);
      let i = startFrom;
      while (numberSet.has(i)) {
        i++;
      }
      return i;
    };

    const getNextSerialNumber = (itemName, metal, currentStock, assignedInThisBatch = []) => {
      if (!itemName || !metal) return 1;

      const allItemsForType = currentStock.filter(item =>
        item.name === itemName && item.metal === metal
      );

      // Get ALL serial numbers (both sold and unsold) from stock for this type
      const allExistingSerialNumbers = allItemsForType
        .map(item => parseInt(item.serialNo) || 0)
        .filter(num => !isNaN(num) && num > 0)
        .sort((a, b) => a - b);

      // Get only UNSOLD items' serial numbers (these are the ones currently in stock)
      const unsoldSerialNumbers = allItemsForType
        .filter(item => item.sold === "No" || item.sold === false)
        .map(item => parseInt(item.serialNo) || 0)
        .filter(num => !isNaN(num) && num > 0);

      // Combine unsold numbers with numbers we've already assigned in this batch
      const currentlyUsedNumbers = [...unsoldSerialNumbers, ...assignedInThisBatch];

      // Find the maximum existing serial number to know where to start new numbers
      const maxExistingNumber = allExistingSerialNumbers.length > 0
        ? Math.max(...allExistingSerialNumbers)
        : 0;

      // First, try to find a gap in the existing numbers (a missing number that was previously sold)
      // Start searching from 1 up to maxExistingNumber
      const missingNumber = findFirstMissingNumber(currentlyUsedNumbers, 1);

      // If the missing number is within the existing range (≤ maxExistingNumber), use it
      if (missingNumber <= maxExistingNumber) {
        return missingNumber;
      }

      // Otherwise, use the next number after the maximum existing number
      return maxExistingNumber + 1;
    };

    const itemsToCancel = [];
    const silverItems = [];

    // Track serial numbers assigned in this batch by item name + metal combination
    const assignedSerialNumbersMap = new Map(); // key: "itemName-metal", value: array of assigned numbers

    for (let i = 0; i < transaction?.items?.length; i++) {
      const item = transaction.items[i];
      console.log('Processing item for cancellation:', item);

      if (!item?.manualItemDetails) {
        const parsedInfo = parseJewelryName(item?.qr);

        if (parsedInfo.isValid) {
          const matchingItems = stock.filter(stockItem =>
            stockItem.qr === item.qr &&
            (stockItem.sold === "Yes" || stockItem.sold === true)
          );

          if (matchingItems.length > 0) {
            const itemToUpdate = matchingItems[0];

            // Create a unique key for this item type and metal combination
            const itemKey = `${itemToUpdate.name}-${itemToUpdate.metal}`;

            // Get or initialize the assigned numbers array for this combination
            if (!assignedSerialNumbersMap.has(itemKey)) {
              assignedSerialNumbersMap.set(itemKey, []);
            }
            const assignedNumbers = assignedSerialNumbersMap.get(itemKey);

            // Check for serial number conflicts
            const conflictingItems = stock.filter(stockItem =>
              stockItem.qr === item.qr
            );

            let newSerialNumber;

            if (conflictingItems.length > 0) {
              console.log('Conflict detected for item:', item.qr, 'Conflicting items:', conflictingItems.length);
              console.log('Item details:', itemToUpdate.name, itemToUpdate.metal);

              // Get next serial number considering both existing stock AND numbers we've already assigned in this batch
              newSerialNumber = getNextSerialNumber(
                itemToUpdate.name,
                itemToUpdate.metal,
                stock,
                assignedNumbers // Pass the numbers we've already assigned in this batch
              );

              // Add this newly assigned number to our tracking map
              assignedNumbers.push(newSerialNumber);

              console.log(`Assigned serial number ${newSerialNumber} for ${itemKey}. Batch assigned numbers:`, assignedNumbers);
            } else {
              newSerialNumber = parsedInfo.serialNo;
            }

            itemsToCancel.push({
              ...itemToUpdate,
              updateData: {
                sold: "No",
                soldDescription: "",
                serialNo: newSerialNumber
              },
              originalSerialNo: parsedInfo.serialNo,
              hasConflict: conflictingItems.length > 0,
              transactionItem: item
            });

            console.log('Added to cancel list:', {
              name: itemToUpdate.name,
              metal: itemToUpdate.metal,
              assignedSerialNo: newSerialNumber,
              totalItemsInBatch: itemsToCancel.length
            });
          }
        }
      } else {
        console.log('Found manual silver item for cancellation:', item?.manualItemDetails);

        // Collect silver items for aggregation
        if (item?.manualItemDetails?.metal === "Silver" && item?.manualItemDetails?.silverType) {
          const silverItem = {
            silverType: item.manualItemDetails.silverType,
            weight: parseFloat(item.manualItemDetails.weight) || 0,
            transactionItem: item
          };
          silverItems.push(silverItem);
        } else if (item?.manualItemDetails?.metal === "Gold") {
          const silverItem = {
            silverType: 'gold',
            weight: parseFloat(item.manualItemDetails.weight) || 0,
            transactionItem: item
          };
          silverItems.push(silverItem);
        }
      }
    }

    // Log the final serial number assignments
    console.log('Final items to cancel with assigned serial numbers:');
    itemsToCancel.forEach((item, index) => {
      console.log(`Item ${index + 1}: ${item.name} (${item.metal}) - Serial No: ${item.updateData.serialNo}`);
    });

    // Aggregate silver items by silverType
    const aggregatedSilverItems = {};
    silverItems.forEach(silverItem => {
      const { silverType, weight, transactionItem } = silverItem;

      if (!aggregatedSilverItems[silverType]) {
        aggregatedSilverItems[silverType] = {
          silverType: silverType,
          totalWeight: 0,
          items: []
        };
      }

      aggregatedSilverItems[silverType].totalWeight += weight;
      aggregatedSilverItems[silverType].items.push({
        ...silverItem,
        transactionItem: transactionItem
      });
    });

    // Convert aggregatedSilverItems to array
    const silverItemsArray = Object.values(aggregatedSilverItems);

    // Check if we have either jewelry items or silver items to cancel
    const hasJewelryItems = itemsToCancel.length > 0;
    const hasSilverItems = silverItemsArray.length > 0;

    if (hasJewelryItems || hasSilverItems) {
      setSelectedCancelTransaction(transaction);
      setCancellingItems(itemsToCancel);
      setSilverItemsArray(silverItemsArray);

      // Set default refund amounts to the transaction's paid amount
      const paidAmount = transaction.summary?.paymentDone || transaction.summary?.grandTotal || 0;
      setRefundAmounts({
        cash: paidAmount,
        bank: 0,
        total: paidAmount
      });

      setCancelModalOpen(true);

      // Log what we found
      if (hasSilverItems) {
        console.log('Silver items aggregated by type:', silverItemsArray);
        silverItemsArray.forEach(silverTypeGroup => {
          console.log(`Silver Type: ${silverTypeGroup.silverType}, Total Weight: ${silverTypeGroup.totalWeight}g`);
        });
      }
    } else {
      alert(`No sold items found in bill ${transaction.transactionDetails?.billNo} to cancel.`);
    }
  }, [stock]);

  const setCancelModal = useCallback((open, transaction) => {
    setCancelModalOpen(open);
    handleCancelBill(transaction);
  }, [handleCancelBill]);

  const handleConfirmCancellation = async () => {
    setIsCancelling(true);
    try {
      // Use the already aggregated silver items from state
      // No need to re-process them
      const silverItems = silverItemsArray.map(silverGroup => ({
        silverType: silverGroup.silverType,
        totalWeight: silverGroup.totalWeight,
        itemCount: silverGroup.items.length,
        items: silverGroup.items.map(item => ({
          itemNumber: item.transactionItem?.itemNumber || 0,
          weight: item.weight,
          name: item.transactionItem?.name || 'Silver Item',
          silverType: item.silverType
        }))
      }));

      // Create the cancellation object with silver items
      const cancellationData = {
        transactionId: selectedCancelTransaction.id,
        billNo: selectedCancelTransaction.transactionDetails?.billNo,
        customerId: selectedCancelTransaction.customer,
        refund: {
          cash: refundAmounts.cash,
          bank: refundAmounts.bank,
          total: refundAmounts.total
        },
        items: cancellingItems.map(item => ({
          id: item.id,
          name: item.name,
          huid: item.huid,
          originalSerialNo: item.originalSerialNo,
          newSerialNo: item.updateData.serialNo,
          hasConflict: item.hasConflict,
          updateData: item.updateData
        })),
        silverItems: silverItems, // Add silver items array
        cancelledAt: new Date().toISOString()
      };

      let resp = await removeCustomerTransactionAndUpdateStock(cancellationData);
      console.log('Cancellation Response:', resp);

      console.log('Cancellation Data with Silver Items:', cancellationData);

      // Show success message
      showAlert(`Bill ${selectedCancelTransaction.transactionDetails?.billNo} has been cancelled successfully!`, 'success');

      // Refresh data
      refresh();

      // Close modal
      handleCancelModalClose();

    } catch (error) {
      console.error('Error cancelling bill:', error);
      showAlert('Failed to cancel bill. Please try again.', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  const ActionsRenderer = useCallback((props) => {
    const { handleViewTransaction, handlePrintBill } = props.context;
    const isMobile = isMobileDevice();

    const isCancelled = (transaction) => {
      return (
        transaction?.transactionDetails?.type === "cancelled" ||
        transaction?.status === "cancelled" ||
        transaction?.isCancelled === true ||
        transaction?.transactionDetails?.isCancelled === true ||
        transaction?.cancelledAt !== undefined
      );
    };

    const cancelled = isCancelled(props.data);

    if (cancelled) {
      return (
        <Box sx={{
          display: 'flex',
          gap: 0.5,
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewTransaction(props.data)}
              sx={{
                color: '#6B7280',
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: 'rgba(107, 114, 128, 0.2)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Cancelled Bill">
            <IconButton
              size="small"
              onClick={() => handlePrintBill(props.data)}
              sx={{
                color: '#6B7280',
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '8px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: 'rgba(107, 114, 128, 0.2)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Chip
            label="Cancelled"
            size="small"
            color="error"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: '24px',
              textDecoration: 'line-through',
            }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{
        display: 'flex',
        gap: 0.5,
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}>
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewTransaction(props.data)}
            sx={{
              color: '#2563EB',
              backgroundColor: 'rgba(37, 99, 235, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
              '&:hover': {
                backgroundColor: 'rgba(37, 99, 235, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={isMobile ? "Share Bill" : "Print Bill"}>
          <IconButton
            size="small"
            onClick={() => handlePrintBill(props.data)}
            disabled={isSharing} // Add this line
            sx={{
              color: isMobile ? '#8B5CF6' : '#10B981',
              backgroundColor: isMobile
                ? 'rgba(139, 92, 246, 0.1)'
                : 'rgba(16, 185, 129, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
              '&:hover': {
                backgroundColor: isMobile
                  ? 'rgba(139, 92, 246, 0.2)'
                  : 'rgba(16, 185, 129, 0.2)',
                transform: 'translateY(-2px)',
              },
              '&.Mui-disabled': {
                opacity: 0.5,
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isSharing ? (
              <CircularProgress size={16} sx={{ color: isMobile ? '#8B5CF6' : '#10B981' }} />
            ) : (
              isMobile ? <ShareIcon fontSize="small" /> : <PrintIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel Bill">
          <IconButton
            size="small"
            onClick={() => setCancelModal(true, props.data)}
            sx={{
              color: '#b91010',
              backgroundColor: 'rgba(185, 16, 16, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              padding: '6px',
              '&:hover': {
                backgroundColor: 'rgba(185, 16, 16, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, [setCancelModal, isSharing]);

  // AG Grid Column Definitions
  const columnDefs = useMemo(() => [
    {
      headerName: '#',
      valueGetter: 'node.rowIndex + 1',
      width: 50,
      sortable: false,
      filter: false,
      pinned: 'left',
      cellStyle: {
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500,
        color: '#6B7280',
        textDecoration: params => params.data?.transactionDetails?.type === "cancelled" ? 'line-through' : 'none',
      },
    },
    {
      field: 'date',
      headerName: 'DATE',
      width: 110,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => {
        try {
          const date = parse(params.value, 'dd/MM/yyyy', new Date());
          return format(date, 'dd MMM');
        } catch {
          return params.value;
        }
      },
      cellStyle: {
        fontWeight: 600,
        color: '#374151',
        fontFamily: "'SF Mono', monospace",
      },
      headerClass: 'custom-header',
    },
    {
      field: 'customer',
      headerName: 'CUSTOMER',
      width: 250,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: CustomerNameRenderer,
      cellRendererParams: { context: { customers } },
      headerClass: 'custom-header',
    },
    {
      field: 'transactionDetails.billNo',
      headerName: 'BILL NO',
      width: 100,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: {
        fontWeight: 700,
        color: '#1F2937',
        fontFamily: "'SF Mono', monospace",
      },
      headerClass: 'custom-header',
    },
    {
      field: 'items',
      headerName: 'ITEMS',
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: ItemsRenderer,
      headerClass: 'custom-header',
    },
    {
      field: 'summary.grandTotal',
      headerName: 'AMOUNT',
      width: 140,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: AmountRenderer,
      headerClass: 'custom-header',
      comparator: (valueA, valueB) => valueA - valueB,
    },
    {
      field: 'summary.pendingAmount',
      headerName: 'STATUS',
      width: 120,
      filter: false,
      sortable: true,
      cellRenderer: StatusRenderer,
      comparator: (valueA, valueB) => (valueA === 0 ? -1 : valueB === 0 ? 1 : 0),
      headerClass: 'custom-header',
    },
    {
      headerName: 'ACTIONS',
      width: 140,
      filter: false,
      sortable: false,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        context: {
          handleViewTransaction: (data) => {
            setSelectedTransaction(data);
            setOpenModal(true);
          },
          handlePrintBill: handlePrintBill,
        }
      },
      pinned: 'right',
      headerClass: 'custom-header',
    },
  ], [customers, handlePrintBill, ActionsRenderer]);

  // Default Column Definition
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
    wrapText: true,
    autoHeight: true,
    cellStyle: (params) => {
      const isCancelled = (transaction) => {
        return (
          transaction?.transactionDetails?.type === "cancelled" ||
          transaction?.status === "cancelled" ||
          transaction?.isCancelled === true ||
          transaction?.transactionDetails?.isCancelled === true ||
          transaction?.cancelledAt !== undefined
        );
      };

      const cancelled = isCancelled(params.data);

      return {
        color: cancelled ? (isDark ? '#9CA3AF' : '#6B7280') : theme.palette.text.primary,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        textDecoration: cancelled ? 'line-through' : 'none',
        opacity: cancelled ? 0.8 : 1,
      };
    },
    headerClass: 'ag-header-cell-premium'
  }), [isDark, theme.palette.text.primary]);

  const isCancelled = (transaction) => {
    return (
      transaction?.transactionDetails?.type === "cancelled" ||
      transaction?.status === "cancelled" ||
      transaction?.isCancelled === true ||
      transaction?.transactionDetails?.isCancelled === true ||
      transaction?.cancelledAt !== undefined
    );
  };

  // Grid Options
  const gridOptions = useMemo(() => ({
    rowBuffer: 20,
    cacheBlockSize: 100,
    maxBlocksInCache: 10,
    animateRows: true,
    rowSelection: 'single',
    enableCellTextSelection: true,
    ensureDomOrder: true,
    suppressColumnVirtualisation: false,
    suppressRowVirtualisation: false,
    getRowStyle: (params) => {
      const cancelled = isCancelled(params.data);

      return {
        backgroundColor: params.node.rowIndex % 2 === 0
          ? (isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
          : 'transparent',
        textDecoration: cancelled ? 'line-through' : 'none',
        opacity: cancelled ? 0.6 : 1,
        color: cancelled ? (isDark ? '#9CA3AF' : '#6B7280') : theme.palette.text.primary,
        // backgroundColor: cancelled ? (isDark ? 'rgba(220, 38, 38, 0.05)' : 'rgba(220, 38, 38, 0.05)') : undefined,
      };
    },
    onGridReady: (params) => {
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', () => {
        setTimeout(() => params.api.sizeColumnsToFit());
      });
    },
    onRowClicked: (params) => {
      params.node.setSelected(true, true);
    },
    onSelectionChanged: (params) => {
      // Handle row selection if needed
    },
  }), [isDark, theme.palette.text.primary]);

  // Handle Export PDF
  const handleExportPDF = useCallback(async () => {
    if (!gridRef.current?.api) {
      showAlert('Grid not ready', 'error');
      return;
    }

    if (filteredTransactions.length === 0) {
      showAlert('No transactions to export', 'warning');
      return;
    }

    setIsExporting(true);

    try {
      showAlert('Generating PDF...', 'info');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const gstTransactions = filteredTransactions.filter(t =>
        t.transactionDetails?.gstApplicable
      );

      if (gstTransactions.length === 0) {
        showAlert('No GST transactions found for export', 'warning');
        return;
      }

      for (let i = 0; i < gstTransactions.length; i++) {
        if (i > 0) {
          pdf.addPage();
        }

        const transaction = gstTransactions[i];
        const billHtml = generateTransactionBillHTML(transaction, customers);

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = billHtml;
        tempDiv.style.width = '210mm';
        tempDiv.style.padding = '0';
        tempDiv.style.margin = '0';
        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = imgWidth / imgHeight;

        const margin = 5;
        const maxWidth = pageWidth - 2 * margin;
        const maxHeight = pageHeight - 2 * margin;

        let finalWidth, finalHeight;

        if (imgWidth > imgHeight) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / ratio;
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = maxHeight * ratio;
          }
        } else {
          finalHeight = maxHeight;
          finalWidth = maxHeight * ratio;
          if (finalWidth > maxWidth) {
            finalWidth = maxWidth;
            finalHeight = maxWidth / ratio;
          }
        }

        const x = (pageWidth - finalWidth) / 2;
        const y = (pageHeight - finalHeight) / 2;

        pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);
        document.body.removeChild(tempDiv);
      }

      const fileName = `Transaction_History_${format(dates.startDate, 'dd-MM-yyyy')}_to_${format(dates.endDate, 'dd-MM-yyyy')}.pdf`;
      pdf.save(fileName);

      showAlert('PDF exported successfully', 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showAlert('Failed to generate PDF', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [dates, customers, showAlert, filteredTransactions]);

  // Handle Export Excel
  const handleExportExcel = useCallback(() => {
    if (!gridRef.current?.api) {
      showAlert('Grid not ready', 'error');
      return;
    }

    // Helper function to calculate metal weights from items
    const calculateMetalWeights = (items) => {
      let goldWeight = 0;
      let silverWeight = 0;

      if (!items || !Array.isArray(items)) {
        return { goldWeight, silverWeight };
      }

      items.forEach(item => {
        const weight = parseFloat(item.weight) || 0;

        // Get metal from multiple possible locations
        const possibleMetal = [
          item.metal,
          item.itemType,
          item.manualItemDetails?.metal,
          item.manualItemDetails?.itemType,
          item.manualItemDetails?.silverType,
          item.name
        ].filter(Boolean).map(str => str.toLowerCase());
        // Check if any of the possible metal fields indicate gold
        const isGold = possibleMetal.some(field =>
          field === 'gold' || field.includes('gold')
        );

        // Check if any of the possible metal fields indicate silver
        const isSilver = possibleMetal.some(field =>
          field === 'silver' || field.includes('silver')
        );

        // If both gold and silver are detected, prefer the explicit metal field
        if (isGold && isSilver) {
          // Prefer the explicit metal field over the name
          const explicitMetal = [
            item.metal,
            item.manualItemDetails?.metal,
            item.manualItemDetails?.silverType
          ].filter(Boolean).map(str => str.toLowerCase());

          if (explicitMetal.some(f => f === 'gold' || f.includes('gold'))) {
            goldWeight += weight;
          } else if (explicitMetal.some(f => f === 'silver' || f.includes('silver'))) {
            silverWeight += weight;
          } else {
            // Fallback: check if name contains specific metal
            const itemName = (item.name || '').toLowerCase();
            if (itemName.includes('gold')) goldWeight += weight;
            else if (itemName.includes('silver')) silverWeight += weight;
          }
        } else if (isGold) {
          goldWeight += weight;
        } else if (isSilver) {
          silverWeight += weight;
        }
      });

      return { goldWeight, silverWeight };
    };

    // Helper function to format item details in a readable way
    const formatItemDetails = (items) => {
      if (!items || !Array.isArray(items) || items.length === 0) {
        return 'No items';
      }

      // Create a readable string for each item
      const itemStrings = items.map((item, idx) => {
        const itemName = item.name || 'Unknown Item';
        const weight = parseFloat(item.weight) || 0;
        const metalRate = parseFloat(item.metalRate) || 0;
        const total = parseFloat(item.total) || 0;
        const huid = item.huid || '';
        const carat = item.ct || '';

        // Build a compact but informative description
        let description = `${idx + 1}. ${itemName}`;
        if (carat) description += ` (${carat})`;
        if (huid) description += ` [HUID:${huid}]`;
        description += ` - ${weight}g`;
        if (metalRate) description += ` @ ₹${metalRate}`;
        description += ` = ₹${total.toFixed(2)}`;

        // Add making charges or labour if available
        if (item.wastage && parseFloat(item.wastage) > 0) {
          description += ` (MC:${item.wastage}%)`;
        } else if (item.labour && parseFloat(item.labour) > 0) {
          description += ` (Labour:₹${item.labour}/g)`;
        }

        return description;
      });

      // Join all items with newline or semicolon for Excel
      return itemStrings.join('\n');
    };

    // Filter ONLY GST transactions (exclude non-GST and cancelled)
    const gstTransactions = filteredTransactions.filter(transaction => {
      // Check if transaction is GST applicable
      const isGST = transaction.transactionDetails?.gstApplicable === true;

      // Check if transaction is cancelled
      const isCancelled =
        transaction?.transactionDetails?.type === "cancelled" ||
        transaction?.status === "cancelled" ||
        transaction?.isCancelled === true ||
        transaction?.transactionDetails?.isCancelled === true ||
        transaction?.cancelledAt !== undefined;

      // Only include GST transactions that are NOT cancelled
      return isGST && !isCancelled;
    });

    if (gstTransactions.length === 0) {
      showAlert('No GST transactions found to export', 'warning');
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = gstTransactions.map((transaction, index) => {
        const customer = customers.find(c => c.id === transaction.customer);

        // Calculate metal weights for this transaction
        const { goldWeight, silverWeight } = calculateMetalWeights(transaction.items);

        // Format item details
        const itemDetails = formatItemDetails(transaction.items);

        return {
          'Sr. No.': index + 1,
          'Date': transaction.date,
          'Customer Name': customer?.name || 'N/A',
          'Bill No': transaction.transactionDetails?.billNo || 'N/A',
          'Items Count': transaction.items?.length || 0,
          // NEW COLUMN - Item Details in readable format
          'Item Details': itemDetails,
          // Metal weight columns
          'Gold Weight (g)': goldWeight.toFixed(2),
          'Silver Weight (g)': silverWeight.toFixed(2),
          'Subtotal': transaction.calculatedTotals?.subtotal || 0,
          'IGST': transaction.summary?.igst || 0,
          'CGST': transaction.summary?.cgst || 0,
          'SGST': transaction.summary?.sgst || 0,
          'GST Type': transaction.transactionDetails?.gstApplicable
            ? (transaction.transactionDetails?.gstType === 'igst' ? 'IGST' : 'CGST/SGST')
            : 'No GST',
          'Grand Total': transaction.summary?.grandTotal || 0,
          'Paid': transaction.summary?.paymentDone || 0,
          'Pending': transaction.summary?.pendingAmount || 0,
          'Status': (transaction.summary?.pendingAmount || 0) === 0 ? 'Paid' : 'Pending',
          'GST': transaction.transactionDetails?.gstApplicable ? 'Yes' : 'No',
        };
      });

      const totals = {
        'Sr. No.': 'TOTAL',
        'Date': '',
        'Customer Name': '',
        'Bill No': '',
        'Items Count': excelData.reduce((sum, row) => sum + row['Items Count'], 0),
        'Item Details': 'Total Summary',
        'Gold Weight (g)': excelData.reduce((sum, row) => sum + parseFloat(row['Gold Weight (g)'] || 0), 0).toFixed(2),
        'Silver Weight (g)': excelData.reduce((sum, row) => sum + parseFloat(row['Silver Weight (g)'] || 0), 0).toFixed(2),
        'Subtotal': excelData.reduce((sum, row) => sum + row['Subtotal'], 0),
        'IGST': excelData.reduce((sum, row) => sum + row['IGST'], 0),
        'CGST': excelData.reduce((sum, row) => sum + row['CGST'], 0),
        'SGST': excelData.reduce((sum, row) => sum + row['SGST'], 0),
        'GST Type': '',
        'Grand Total': excelData.reduce((sum, row) => sum + row['Grand Total'], 0),
        'Paid': excelData.reduce((sum, row) => sum + row['Paid'], 0),
        'Pending': excelData.reduce((sum, row) => sum + row['Pending'], 0),
        'Status': '',
        'GST': '',
      };

      // Add totals row
      excelData.push({}); // Empty row
      excelData.push(totals);

      const headers = Object.keys(excelData[0]);
      const dataWithHeaders = [
        headers, // Header row
        ...excelData.map(row => headers.map(key => row[key]))
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);

      // Set column widths
      const colWidths = [
        { wch: 8 },   // Sr. No.
        { wch: 12 },  // Date
        { wch: 25 },  // Customer Name
        { wch: 10 },  // Bill No
        { wch: 12 },  // Items Count
        { wch: 60 },  // Item Details - Wider to accommodate item descriptions
        { wch: 15 },  // Gold Weight (g)
        { wch: 15 },  // Silver Weight (g)
        { wch: 15 },  // Subtotal
        { wch: 12 },  // IGST
        { wch: 12 },  // CGST
        { wch: 12 },  // SGST
        { wch: 14 },  // GST Type
        { wch: 15 },  // Grand Total
        { wch: 12 },  // Paid
        { wch: 12 },  // Pending
        { wch: 10 },  // Status
        { wch: 8 },   // GST
      ];
      worksheet['!cols'] = colWidths;

      // Enable text wrapping for the Item Details column
      if (worksheet['!rows'] === undefined) worksheet['!rows'] = [];

      // Apply text wrapping to all rows in the Item Details column
      const itemDetailsColIndex = headers.indexOf('Item Details');
      if (itemDetailsColIndex !== -1) {
        for (let row = 1; row <= dataWithHeaders.length; row++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: itemDetailsColIndex });
          if (worksheet[cellAddress]) {
            worksheet[cellAddress].s = {
              alignment: {
                wrapText: true,
                vertical: "top",
                horizontal: "left"
              }
            };
          }
        }
      }

      const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
      headerRange.s.r = 0; // Start at row 0
      headerRange.e.r = 0; // End at row 0

      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cellAddress]) continue;

        // Apply styling to header cells
        worksheet[cellAddress].s = {
          font: { bold: true, sz: 11 },
          fill: { fgColor: { rgb: "E0E0E0" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      // Auto-set row heights based on content
      worksheet['!rows'] = worksheet['!rows'] || [];
      for (let row = 1; row < dataWithHeaders.length; row++) {
        // Set row height to auto for better visibility of multi-line item details
        worksheet['!rows'][row] = { hpt: 45 };
      }

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'GST Transactions');

      // Generate Excel file
      const fileName = `GST_Transactions_${format(dates.startDate, 'dd-MM-yyyy')}_to_${format(dates.endDate, 'dd-MM-yyyy')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      showAlert(`Exported ${gstTransactions.length} GST transactions successfully`, 'success');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showAlert('Failed to export Excel file', 'error');
    }
  }, [customers, dates, showAlert, filteredTransactions]);

  // Handle Clear Filters
  const handleClearFilters = useCallback(() => {
    setCustomerFilter('all');
    // setSelectedCustomer(null);
    setSearchInput('');
    showAlert('Filters cleared', 'info');
  }, [showAlert]);

  // Custom Loading Overlay
  const loadingOverlayComponent = useMemo(() => {
    return function CustomLoadingOverlay() {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress
              size={50}
              sx={{
                color: '#2563EB',
                mb: 2,
              }}
            />
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: isDark ? '#e0e0e0' : '#1a1a1a',

                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Loading transactions...
            </Typography>
          </Box>
        </Box>
      );
    };
  }, [isDark]);

  // Custom No Rows Overlay
  const noRowsOverlayComponent = useMemo(() => {
    return function CustomNoRowsOverlay() {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.4), rgba(30, 41, 59, 0.6))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.9))',
            backdropFilter: 'blur(10px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          }}>
            <ReceiptIcon sx={{
              fontSize: 40,
              color: isDark ? '#94A3B8' : '#9CA3AF',
              opacity: 0.6,
            }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: isDark ? '#A0A0A0' : '#666',
              fontWeight: 500,
            }}
          >
            {searchInput ? 'No transactions found' : 'No transactions available'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? '#71717A' : '#9CA3AF',
              textAlign: 'center',
              maxWidth: 300,
            }}
          >
            {searchInput
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'Transactions will appear here once you create them.'}
          </Typography>
        </Box>
      );
    };
  }, [isDark, searchInput]);

  // Loading states
  const isLoading = contextLoading || isPageLoading;
  const isProcessing = isSearching || isFiltering || isExporting;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: '#2563EB',
              mb: 2,
              background: 'linear-gradient(135deg, #2563EB, #3B82F6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          />
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              background: isDark
                ? 'linear-gradient(135deg, #60A5FA, #93C5FD)'
                : 'linear-gradient(135deg, #2563EB, #3B82F6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Loading transactions...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: '100vh',
          py: 4,

        }}
      >
        {/* Backdrop for loading states */}
        <Backdrop
          open={isExporting}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: '#fff',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress color="inherit" size={50} />
            <Typography sx={{ mt: 2, fontWeight: 600 }}>Generating PDF...</Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              This may take a moment
            </Typography>
          </Box>
        </Backdrop>

        <Container maxWidth="xl">
          {/* Alert */}
          {alertMessage && (
            <Fade in={!!alertMessage}>
              <Alert
                severity={alertType}
                sx={{
                  mb: 3,
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                  border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  backgroundColor: isDark
                    ? 'rgba(30, 41, 59, 0.7)'
                    : 'rgba(255, 255, 255, 0.8)',
                }}
                onClose={() => setAlertMessage('')}
              >
                {alertMessage}
              </Alert>
            </Fade>
          )}

          {/* Header */}
          <Box sx={{
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 3,
          }}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  mb: 0.5,
                  background: isDark
                    ? 'linear-gradient(135deg, #F1F5F9, #E2E8F0)'
                    : 'linear-gradient(135deg, #1E293B, #374151)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Transaction History
              </Typography>
              <Typography
                sx={{
                  color: isDark ? '#A0A0A0' : '#6B7280',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <HistoryIcon fontSize="small" />
                View and manage customer transactions
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', md: 'flex-end' },
            }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={refresh}
                variant="outlined"
                disabled={isFiltering}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                  color: theme.palette.mode === 'dark' ? '#60A5FA' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(37, 99, 235, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(96, 165, 250, 0.25)'
                      : 'rgba(37, 99, 235, 0.25)',
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 12px rgba(96, 165, 250, 0.4)'
                      : '0 4px 12px rgba(37, 99, 235, 0.3)',
                  },
                  '&.Mui-disabled': {
                    borderColor: theme.palette.mode === 'dark'
                      ? 'rgba(96, 165, 250, 0.2)'
                      : 'rgba(37, 99, 235, 0.2)',
                    color: theme.palette.mode === 'dark'
                      ? 'rgba(96, 165, 250, 0.4)'
                      : 'rgba(37, 99, 235, 0.4)',
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(30, 41, 59, 0.2)'
                      : 'rgba(37, 99, 235, 0.08)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Refresh
              </Button>

              <Button
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                variant="outlined"
                disabled={customerFilter === 'all' && !searchInput}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                  color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(107, 114, 128, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.25)'
                      : 'rgba(107, 114, 128, 0.25)',
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 12px rgba(148, 163, 184, 0.3)'
                      : '0 4px 12px rgba(107, 114, 128, 0.2)',
                  },
                  '&.Mui-disabled': {
                    borderColor: theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.2)'
                      : 'rgba(107, 114, 128, 0.2)',
                    color: theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.9)'
                      : 'rgba(107, 114, 128, 0.9)',
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(128, 132, 141, 0.62)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Clear Filters
              </Button>

              <Button
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                variant="outlined"
                disabled={filteredTransactions.length === 0}
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? '#34D399' : '#10B981',
                  color: theme.palette.mode === 'dark' ? '#34D399' : '#fff',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(16, 185, 129, 0.8)',
                  borderWidth: '1.5px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(52, 211, 153, 0.25)'
                      : 'rgba(16, 185, 129, 0.25)',
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 4px 12px rgba(52, 211, 153, 0.3)'
                      : '0 4px 12px rgba(16, 185, 129, 0.25)',
                  },
                  '&.Mui-disabled': {
                    borderColor: theme.palette.mode === 'dark'
                      ? 'rgba(52, 211, 153, 0.2)'
                      : 'rgba(16, 185, 129, 0.2)',
                    color: theme.palette.mode === 'dark'
                      ? 'rgba(173, 216, 200, 0.9)'
                      : 'rgba(176, 198, 191, 0.9)',
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(16, 185, 129, 0.5)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Export Excel
              </Button>

              <Button
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                variant="contained"
                disabled={isExporting || filteredTransactions.length === 0}
                sx={{
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #DC2626, #B91C1C)'
                    : 'linear-gradient(135deg, #DC2626, #B91C1C)',
                  borderRadius: '10px',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(220, 38, 38, 0.5)'
                    : '0 4px 12px rgba(220, 38, 38, 0.4)',
                  fontWeight: 600,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(220, 38, 38, 0.5)'
                    : '1px solid rgba(220, 38, 38, 0.4)',
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, #B91C1C, #991B1B)'
                      : 'linear-gradient(135deg, #B91C1C, #991B1B)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 6px 20px rgba(220, 38, 38, 0.6)'
                      : '0 6px 20px rgba(220, 38, 38, 0.5)',
                    transform: 'translateY(-2px)',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(220, 38, 38, 0.7)'
                      : '1px solid rgba(220, 38, 38, 0.6)',
                  },
                  '&.Mui-disabled': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(185, 28, 28, 0.4)'
                      : 'rgba(185, 28, 28, 0.4)',
                    color: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.6)'
                      : 'rgba(255, 255, 255, 0.7)',
                    boxShadow: 'none',
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(185, 28, 28, 0.3)'
                      : '1px solid rgba(185, 28, 28, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Export PDF
              </Button>
            </Box>
          </Box>

          {/* Stats Summary */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15), rgba(59, 130, 246, 0.15))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}>
                      <ReceiptIcon sx={{ color: '#2563EB' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Transactions
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563EB', mb: 1 }}>
                    {filteredTransactions.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredTransactions.length > 0 ? (
                      <>
                        <TrendingUpIcon sx={{ fontSize: '12px', verticalAlign: 'middle', mr: 0.5 }} />
                        {((filteredTransactions.length / customerTransactions.length) * 100).toFixed(1)}% of total
                      </>
                    ) : 'No transactions'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(34, 197, 94, 0.15))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}>
                      <TrendingUpIcon sx={{ color: '#10B981' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#10B981', mb: 1 }}>
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      minimumFractionDigits: 0,
                    }).format(filteredTransactions.reduce((sum, t) => sum + parseInt(t.summary?.grandTotal || 0), 0))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      minimumFractionDigits: 0,
                    }).format(filteredTransactions.length > 0 ?
                      filteredTransactions.reduce((sum, t) => sum + parseInt(t.summary?.grandTotal || 0), 0) / filteredTransactions.length : 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}>
                      <PaymentIcon sx={{ color: '#EF4444' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Pending
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#EF4444', mb: 1 }}>
                    {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      minimumFractionDigits: 0,
                    }).format(filteredTransactions.reduce((sum, t) => sum + parseInt(t.summary?.pendingAmount || 0), 0))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredTransactions.filter(t => t.summary?.pendingAmount > 0).length} pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                borderRadius: '16px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.15))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}>
                      <CheckCircleIcon sx={{ color: '#22C55E' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Fully Paid
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#22C55E', mb: 1 }}>
                    {filteredTransactions.filter(t => t.summary?.pendingAmount === 0).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filteredTransactions.length > 0 ?
                      ((filteredTransactions.filter(t => t.summary?.pendingAmount === 0).length / filteredTransactions.length) * 100).toFixed(1) : 0}% paid
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Active Filters Summary */}
          {(customerFilter !== 'all' || searchInput) && (
            <Fade in>
              <Box sx={{
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                padding: 2,
                borderRadius: '12px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
                backdropFilter: 'blur(10px)',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
              }}>
                <FilterIcon sx={{ color: '#6B7280', fontSize: '1rem' }} />
                <Typography variant="body2" color="text.secondary">
                  Active filters:
                </Typography>

                {customerFilter !== 'all' && (
                  <Chip
                    label={`Customer: ${customers.find(c => c.id === customerFilter)?.name || 'Selected Customer'}`}
                    size="small"
                    onDelete={() => {
                      setCustomerFilter('all');
                      // setSelectedCustomer(null);
                    }}
                    color="primary"
                    variant="filled"
                    sx={{
                      backdropFilter: 'blur(10px)',
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      border: '1px solid rgba(37, 99, 235, 0.3)',
                      color: '#2563EB',
                      fontWeight: 500,
                    }}
                  />
                )}

                {searchInput && (
                  <Chip
                    label={`Search: "${searchInput}"`}
                    size="small"
                    onDelete={() => setSearchInput('')}
                    color="secondary"
                    variant="filled"
                    sx={{
                      backdropFilter: 'blur(10px)',
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      color: '#8B5CF6',
                      fontWeight: 500,
                    }}
                  />
                )}

                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Showing {filteredTransactions.length} of {customerTransactions.length} transactions
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Filters Section */}
          <Card sx={{
            mb: 4,
            borderRadius: '16px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))',
            backdropFilter: 'blur(10px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Start Date"
                    value={dates.startDate}
                    onChange={(newValue) => setDates(prev => ({ ...prev, startDate: newValue }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="End Date"
                    value={dates.endDate}
                    onChange={(newValue) => setDates(prev => ({ ...prev, endDate: newValue }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    options={[{ id: 'all', name: 'All Customers' }, ...customers]}
                    value={customerFilter === 'all'
                      ? { id: 'all', name: 'All Customers' }
                      : customers.find(c => c.id === customerFilter) || null}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(event, newValue) => {
                      setCustomerFilter(newValue ? newValue.id : 'all');
                      // setSelectedCustomer(newValue && newValue.id !== 'all' ? newValue : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        placeholder="Search by name, phone, address..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backdropFilter: 'blur(10px)',
                            backgroundColor: isDark
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.02)',
                            width: '300px',
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      if (option.id === 'all') {
                        return (
                          <Box component="li" {...props} key="all">
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              {option.name}
                            </Typography>
                          </Box>
                        );
                      }

                      return (
                        <Box component="li" {...props} key={option.id}>
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              {option.contact && (
                                <Chip
                                  size="small"
                                  label={option.contact}
                                  variant="outlined"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    backdropFilter: 'blur(10px)',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options.slice(0, 50);

                      const searchTerm = inputValue.toLowerCase();
                      return options.filter(option => {
                        if (option.id === 'all') return true;
                        return (
                          option.name.toLowerCase().includes(searchTerm) ||
                          option.contact?.toString().includes(searchTerm)
                        );
                      });
                    }}
                    noOptionsText={
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        No matching customers found
                      </Typography>
                    }
                    loading={customers.length === 0}
                    loadingText="Loading customers..."
                    blurOnSelect
                    clearOnBlur={false}
                    sx={{
                      '& .MuiAutocomplete-listbox': {
                        maxHeight: 400,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: isDark
                          ? 'rgba(30, 41, 59, 0.95)'
                          : 'rgba(255, 255, 255, 0.95)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* AG Grid Table */}
          <Card
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              height: 'calc(100vh - 300px)',
              minHeight: '500px',
              background: isDark
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.2))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2))',
              backdropFilter: 'blur(10px)',
              border: isDark
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            {isProcessing && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                  height: 3,
                  background: 'linear-gradient(90deg, #2563EB, #3B82F6)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #3B82F6, #60A5FA)',
                  },
                }}
              />
            )}

            <CardContent sx={{ p: 0, height: '100%' }}>
              <div
                className={`ag-theme-premium-${theme.palette.mode}`}
                style={{
                  width: '100%',
                  height: '100%',
                }}
              >
                <AgGridReact
                  ref={gridRef}
                  columnDefs={columnDefs}
                  rowData={filteredTransactions}
                  defaultColDef={defaultColDef}
                  gridOptions={gridOptions}
                  loadingOverlayComponent={loadingOverlayComponent}
                  noRowsOverlayComponent={noRowsOverlayComponent}
                  headerHeight={50}
                  rowHeight={64}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                  suppressContextMenu={true}
                  suppressMenuHide={false}
                  suppressMovableColumns={true}
                  getRowStyle={(params) => ({
                    borderBottom: isDark
                      ? '1px solid rgba(255, 255, 255, 0.05)'
                      : '1px solid rgba(0, 0, 0, 0.03)',
                    transition: 'background-color 0.2s ease',
                  })}
                  onGridReady={(params) => {
                    // Add custom header styling
                    const headerElements = document.querySelectorAll('.ag-header-cell');
                    headerElements.forEach(header => {
                      header.style.backdropFilter = 'blur(10px)';
                      header.style.borderRight = 'none';
                      header.style.fontWeight = '600';
                      header.style.letterSpacing = '0.5px';
                      header.style.textTransform = 'uppercase';
                      header.style.fontSize = '11px';
                      header.style.color = isDark ? '#94A3B8' : '#6B7280';
                    });
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Transaction Details Modal */}
          <Dialog
            open={openModal}
            onClose={() => setOpenModal(false)}
            maxWidth="xl"
            fullWidth
            TransitionComponent={Zoom}
            TransitionProps={{ timeout: 300 }}
            PaperProps={{
              sx: {
                borderRadius: '24px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
                backdropFilter: 'blur(30px)',
                border: `1px solid ${alpha(
                  isDark ? '#475569' : '#e2e8f0',
                  isDark ? 0.2 : 0.3
                )}`,
                boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
                maxHeight: '95vh',
                overflow: 'hidden',
              },
            }}
          >
            {selectedTransaction && (
              <>
                {/* Header Section */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${alpha(
                      isDark ? '#475569' : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    background: isDark
                      ? 'linear-gradient(90deg, rgba(96, 165, 250, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)'
                      : 'linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #60a5fa, #3b82f6, #60a5fa)',
                      animation: 'shine 3s infinite linear',
                    },
                    '@keyframes shine': {
                      '0%': { backgroundPosition: '-200px 0' },
                      '100%': { backgroundPosition: 'calc(200px + 100%) 0' },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Box
                          sx={{
                            width: 45,
                            height: 45,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                            boxShadow: '0 8px 32px rgba(96, 165, 250, 0.3)',
                          }}
                        >
                          <ReceiptIcon sx={{ fontSize: 25, color: 'white' }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              color: isDark ? '#f1f5f9' : '#1e293b',
                              mb: 0.5,
                            }}
                          >
                            Transaction Details
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              color: isDark ? '#94a3b8' : '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontWeight: 500,
                            }}
                          >
                            <CalendarToday sx={{ fontSize: 18 }} />
                            {format(parse(selectedTransaction.date, 'dd/MM/yyyy', new Date()), 'EEEE, dd MMMM yyyy')}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Bill Number & Status */}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                        <Chip
                          icon={<ReceiptIcon />}
                          label={`Bill: ${selectedTransaction.transactionDetails?.billNo || 'N/A'}`}
                          sx={{
                            backgroundColor: alpha('#3b82f6', 0.15),
                            color: '#3b82f6',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            border: `1px solid ${alpha('#3b82f6', 0.3)}`,
                            backdropFilter: 'blur(10px)',
                          }}
                        />
                        <Chip
                          icon={<PersonIcon />}
                          label={`Customer: ${getCustomerName(selectedTransaction.customer)}`}
                          sx={{
                            backgroundColor: alpha('#10b981', 0.15),
                            color: '#10b981',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            border: `1px solid ${alpha('#10b981', 0.3)}`,
                            backdropFilter: 'blur(10px)',
                          }}
                        />
                        <Chip
                          icon={selectedTransaction.transactionDetails?.gstApplicable ? '📋' : '📄'}
                          label={selectedTransaction.transactionDetails?.gstApplicable
                            ? `GST: ${selectedTransaction.transactionDetails?.gstType === 'igst' ? 'IGST' : 'CGST/SGST'}`
                            : 'No GST'}
                          sx={{
                            backgroundColor: selectedTransaction.transactionDetails?.gstApplicable
                              ? selectedTransaction.transactionDetails?.gstType === 'igst'
                                ? alpha('#f59e0b', 0.15)
                                : alpha('#059669', 0.15)
                              : alpha('#6b7280', 0.15),
                            color: selectedTransaction.transactionDetails?.gstApplicable
                              ? selectedTransaction.transactionDetails?.gstType === 'igst'
                                ? '#d97706'
                                : '#059669'
                              : '#6b7280',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            border: `1px solid ${alpha(
                              selectedTransaction.transactionDetails?.gstApplicable
                                ? selectedTransaction.transactionDetails?.gstType === 'igst' ? '#f59e0b' : '#059669'
                                : '#6b7280',
                              0.3
                            )}`,
                            backdropFilter: 'blur(10px)',
                          }}
                        />
                        <Chip
                          icon={
                            selectedTransaction.summary?.pendingAmount > 0 ? '⏳' : '✅'
                          }
                          label={
                            selectedTransaction.summary?.pendingAmount > 0
                              ? `Pending: ₹${selectedTransaction.summary.pendingAmount.toLocaleString('en-IN')}`
                              : 'Fully Paid'
                          }
                          sx={{
                            backgroundColor: selectedTransaction.summary?.pendingAmount > 0
                              ? alpha('#f59e0b', 0.15)
                              : alpha('#10b981', 0.15),
                            color: selectedTransaction.summary?.pendingAmount > 0
                              ? '#f59e0b'
                              : '#10b981',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            border: `1px solid ${alpha(
                              selectedTransaction.summary?.pendingAmount > 0 ? '#f59e0b' : '#10b981',
                              0.3
                            )}`,
                            backdropFilter: 'blur(10px)',
                          }}
                        />
                      </Box>
                    </Box>

                    <IconButton
                      onClick={() => setOpenModal(false)}
                      sx={{
                        backgroundColor: isDark
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.05)',
                        color: isDark ? '#94a3b8' : '#64748b',
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        '&:hover': {
                          backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(0, 0, 0, 0.1)',
                          transform: 'rotate(90deg)',
                          transition: 'transform 0.3s ease',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Content Section */}
                <Box sx={{ p: 4, overflow: 'auto', maxHeight: '60vh' }}>
                  <Grid container spacing={3}>
                    {/* Left Column - Items Table */}
                    <Grid item xs={12} lg={7}>
                      <Paper
                        sx={{
                          p: 3,
                          borderRadius: '20px',
                          background: isDark
                            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                          backdropFilter: 'blur(20px)',
                          border: `1px solid ${alpha(
                            isDark ? '#475569' : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          height: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 3,
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 800,
                              color: isDark ? '#f1f5f9' : '#1e293b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                              }}
                            >
                              <InventoryIcon sx={{ fontSize: 24, color: 'white' }} />
                            </Box>
                            Items Purchased ({selectedTransaction.items?.length || 0})
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: '16px',
                            overflow: 'hidden',
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.2 : 0.3
                            )}`,
                            background: isDark
                              ? 'rgba(15, 23, 42, 0.5)'
                              : 'rgba(255, 255, 255, 0.7)',
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.9)'
                                        : 'rgba(248, 250, 252, 0.95)',
                                      color: isDark ? '#94a3b8' : '#64748b',
                                      fontWeight: 700,
                                      fontSize: '0.875rem',
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? '#475569' : '#e2e8f0',
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                      py: 1.5,
                                    }}
                                  >
                                    #
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.9)'
                                        : 'rgba(248, 250, 252, 0.95)',
                                      color: isDark ? '#94a3b8' : '#64748b',
                                      fontWeight: 700,
                                      fontSize: '0.875rem',
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? '#475569' : '#e2e8f0',
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Item Name
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.9)'
                                        : 'rgba(248, 250, 252, 0.95)',
                                      color: isDark ? '#94a3b8' : '#64748b',
                                      fontWeight: 700,
                                      fontSize: '0.875rem',
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? '#475569' : '#e2e8f0',
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    HUID
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.9)'
                                        : 'rgba(248, 250, 252, 0.95)',
                                      color: isDark ? '#94a3b8' : '#64748b',
                                      fontWeight: 700,
                                      fontSize: '0.875rem',
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? '#475569' : '#e2e8f0',
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Weight (g)
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.9)'
                                        : 'rgba(248, 250, 252, 0.95)',
                                      color: isDark ? '#94a3b8' : '#64748b',
                                      fontWeight: 700,
                                      fontSize: '0.875rem',
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? '#475569' : '#e2e8f0',
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Rate
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.9)'
                                        : 'rgba(248, 250, 252, 0.95)',
                                      color: isDark ? '#94a3b8' : '#64748b',
                                      fontWeight: 700,
                                      fontSize: '0.875rem',
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? '#475569' : '#e2e8f0',
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Wastage %
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.9)'
                                        : 'rgba(248, 250, 252, 0.95)',
                                      color: isDark ? '#94a3b8' : '#64748b',
                                      fontWeight: 700,
                                      fontSize: '0.875rem',
                                      borderBottom: `2px solid ${alpha(
                                        isDark ? '#475569' : '#e2e8f0',
                                        isDark ? 0.3 : 0.4
                                      )}`,
                                    }}
                                  >
                                    Amount
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedTransaction.items?.map((item, index) => (
                                  <TableRow
                                    key={index}
                                    hover
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: isDark
                                          ? 'rgba(255, 255, 255, 0.05)'
                                          : 'rgba(0, 0, 0, 0.02)',
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.2s ease',
                                      },
                                      transition: 'all 0.2s ease',
                                      position: 'relative',
                                      '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: 1,
                                        background: isDark
                                          ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)'
                                          : 'linear-gradient(90deg, transparent, rgba(0,0,0,0.03), transparent)',
                                      },
                                    }}
                                  >
                                    <TableCell sx={{ py: 1.5 }}>
                                      <Box
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          borderRadius: '8px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          background: alpha('#3b82f6', 0.1),
                                          color: '#3b82f6',
                                          fontWeight: 700,
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        {index + 1}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 600, color: isDark ? '#f1f5f9' : '#1e293b' }}
                                        >
                                          {item.name}
                                        </Typography>
                                        {item.category && (
                                          <Chip
                                            label={item.category}
                                            size="small"
                                            sx={{
                                              mt: 0.5,
                                              backgroundColor: alpha('#10b981', 0.1),
                                              color: '#10b981',
                                              fontSize: '0.625rem',
                                              height: 20,
                                              border: `1px solid ${alpha('#10b981', 0.2)}`,
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: isDark ? '#94a3b8' : '#64748b',
                                          fontFamily: 'monospace',
                                        }}
                                      >
                                        {item.huid || '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: isDark ? '#f1f5f9' : '#1e293b' }}
                                      >
                                        {item.weight}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          backgroundColor: alpha('#f59e0b', 0.1),
                                          padding: '2px 8px',
                                          borderRadius: '6px',
                                          border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 600, color: '#f59e0b' }}
                                        >
                                          ₹{item.metalRate}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: isDark ? '#f1f5f9' : '#1e293b' }}
                                      >
                                        {item.wastage}%
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          backgroundColor: alpha('#10b981', 0.1),
                                          padding: '2px 8px',
                                          borderRadius: '6px',
                                          border: `1px solid ${alpha('#10b981', 0.2)}`,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 700, color: '#10b981' }}
                                        >
                                          ₹{item.total.toLocaleString('en-IN')}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Paper>
                    </Grid>
                    {/* Right Column - Financial Summary */}
                    <Grid item xs={12} lg={5}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                        {/* Payment Summary Card */}
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: '20px',
                            background: isDark
                              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 3,
                              fontWeight: 800,
                              color: isDark ? '#f1f5f9' : '#1e293b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
                              }}
                            >
                              <PaymentIcon sx={{ fontSize: 20, color: 'white' }} />
                            </Box>
                            Payment Summary
                          </Typography>

                          <Stack spacing={2}>
                            {/* Regular Payments */}
                            {selectedTransaction.paymentDetails && Object.entries(selectedTransaction.paymentDetails).map(([key, value]) => {
                              if (key === 'advancePayment' || key === 'totalPaymentDone' || key === 'discount') return null;

                              return (
                                <Box
                                  key={key}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: '10px',
                                    background: isDark
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : 'rgba(0, 0, 0, 0.02)',
                                    border: `1px solid ${alpha(
                                      isDark ? '#475569' : '#e2e8f0',
                                      isDark ? 0.1 : 0.2
                                    )}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      background: isDark
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : 'rgba(0, 0, 0, 0.04)',
                                      transform: 'translateX(4px)',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: alpha('#3b82f6', 0.1),
                                        border: `1px solid ${alpha('#3b82f6', 0.2)}`,
                                      }}
                                    >
                                      {key === 'cash' && <LocalAtmIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                                      {key === 'cheque' && <AccountBalanceIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                                      {key === 'online' && <PaidIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                                      {!['cash', 'cheque', 'online'].includes(key) && <PaymentIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: isDark ? '#f1f5f9' : '#1e293b',
                                        textTransform: 'capitalize',
                                      }}
                                    >
                                      {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 700,
                                      color: '#10b981',
                                      fontSize: '1rem',
                                    }}
                                  >
                                    ₹{value.toLocaleString('en-IN')}
                                  </Typography>
                                </Box>
                              );
                            })}

                            {/* Discount */}
                            {selectedTransaction.paymentDetails?.discount > 0 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  py: 1.5,
                                  px: 2,
                                  borderRadius: '10px',
                                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                                  border: `1px solid ${alpha('#10b981', 0.2)}`,
                                  mt: 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: alpha('#10b981', 0.15),
                                      border: `1px solid ${alpha('#10b981', 0.3)}`,
                                    }}
                                  >
                                    <DiscountIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: '#10b981' }}
                                  >
                                    Discount
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#10b981',
                                    fontSize: '1rem',
                                  }}
                                >
                                  -₹{selectedTransaction.paymentDetails.discount.toLocaleString('en-IN')}
                                </Typography>
                              </Box>
                            )}

                            {/* Advance Payment Section */}
                            {selectedTransaction.paymentDetails?.advancePayment?.enabled && (
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 2,
                                  borderTop: `1px solid ${alpha('#f59e0b', 0.2)}`,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 2,
                                    p: 1.5,
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)',
                                    border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                                  }}
                                >
                                  <TrendingUpIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 700, color: '#f59e0b' }}
                                  >
                                    Advance Payment
                                  </Typography>
                                  <Chip
                                    label={`₹${selectedTransaction.paymentDetails.advancePayment.total?.toLocaleString('en-IN') || 0}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha('#f59e0b', 0.2),
                                      color: '#f59e0b',
                                      fontWeight: 700,
                                      border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                                    }}
                                  />
                                </Box>

                                {selectedTransaction.paymentDetails.advancePayment.breakup && (
                                  <Stack spacing={1.5}>
                                    {Object.entries(selectedTransaction.paymentDetails.advancePayment.breakup).map(([breakupKey, breakupValue]) => {
                                      if (breakupKey === 'customOptions' || !breakupValue) return null;

                                      return (
                                        <Box
                                          key={breakupKey}
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 1,
                                            px: 2,
                                            borderRadius: '8px',
                                            background: isDark
                                              ? 'rgba(255, 255, 255, 0.03)'
                                              : 'rgba(0, 0, 0, 0.01)',
                                            border: `1px solid ${alpha('#f59e0b', 0.1)}`,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: isDark ? '#94a3b8' : '#64748b',
                                              textTransform: 'capitalize',
                                            }}
                                          >
                                            {breakupKey.replace(/([A-Z])/g, ' $1').trim()}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: '#f59e0b',
                                            }}
                                          >
                                            ₹{breakupValue.toLocaleString('en-IN')}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                )}
                              </Box>
                            )}

                            {/* Total Paid */}
                            {selectedTransaction.paymentDetails?.totalPaymentDone && (
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 2,
                                  borderTop: `1px solid ${alpha('#10b981', 0.3)}`,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
                                    border: `1px solid ${alpha('#10b981', 0.3)}`,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 700, color: '#10b981' }}
                                  >
                                    Total Paid
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: '#10b981',
                                    }}
                                  >
                                    ₹{selectedTransaction.paymentDetails.totalPaymentDone.toLocaleString('en-IN')}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Paper>

                        {/* Totals Summary Card */}
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: '20px',
                            background: isDark
                              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 3,
                              fontWeight: 800,
                              color: isDark ? '#f1f5f9' : '#1e293b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
                              }}
                            >
                              <TrendingUpIcon sx={{ fontSize: 20, color: 'white' }} />
                            </Box>
                            Financial Summary
                          </Typography>

                          <Stack spacing={2}>
                            {/* Subtotal */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                              }}
                            >
                              <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                Subtotal
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                ₹{selectedTransaction.calculatedTotals?.subtotal?.toLocaleString('en-IN') || 0}
                              </Typography>
                            </Box>

                            {/* GST if applicable - Support both CGST/SGST and IGST */}
                            {selectedTransaction.transactionDetails?.gstApplicable && (
                              <>
                                {selectedTransaction.summary?.igst > 0 ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      py: 1.5,
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                      IGST (3%)
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                                      ₹{selectedTransaction.summary?.igst?.toLocaleString('en-IN') || 0}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 1.5,
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        CGST (1.5%)
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                                        ₹{selectedTransaction.summary?.cgst?.toLocaleString('en-IN') || 0}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 1.5,
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        SGST (1.5%)
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                                        ₹{selectedTransaction.summary?.sgst?.toLocaleString('en-IN') || 0}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </>
                            )}

                            {/* Divider */}
                            <Divider sx={{ borderColor: isDark ? '#475569' : '#e2e8f0' }} />

                            {/* Grand Total - Highlighted */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 2,
                                px: 2,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)',
                                border: `1px solid ${alpha('#2563eb', 0.2)}`,
                                mt: 1,
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: isDark ? '#f1f5f9' : '#1e293b',
                                }}
                              >
                                Grand Total
                              </Typography>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 900,
                                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  backgroundClip: 'text',
                                }}
                              >
                                ₹{selectedTransaction.summary?.grandTotal?.toLocaleString('en-IN') || 0}
                              </Typography>
                            </Box>

                            {/* Pending Amount */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1.5,
                                px: 2,
                                borderRadius: '10px',
                                background: selectedTransaction.summary?.pendingAmount > 0
                                  ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)'
                                  : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                                border: `1px solid ${alpha(
                                  selectedTransaction.summary?.pendingAmount > 0 ? '#ef4444' : '#10b981',
                                  0.2
                                )}`,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {selectedTransaction.summary?.pendingAmount > 0 ? (
                                  <ErrorOutlineIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                                ) : (
                                  <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#10b981' }} />
                                )}
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 700,
                                    color: selectedTransaction.summary?.pendingAmount > 0 ? '#ef4444' : '#10b981',
                                  }}
                                >
                                  {selectedTransaction.summary?.pendingAmount > 0 ? 'Pending Amount' : 'Payment Status'}
                                </Typography>
                              </Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: selectedTransaction.summary?.pendingAmount > 0 ? '#ef4444' : '#10b981',
                                }}
                              >
                                {selectedTransaction.summary?.pendingAmount > 0 ? '₹' + selectedTransaction.summary.pendingAmount.toLocaleString('en-IN') : 'Fully Paid'}
                              </Typography>
                            </Box>

                            {/* Payment Progress */}
                            {selectedTransaction.summary?.pendingAmount > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 1, display: 'block' }}>
                                  Payment Progress
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    ((selectedTransaction.summary?.grandTotal - selectedTransaction.summary?.pendingAmount) /
                                      selectedTransaction.summary?.grandTotal) *
                                    100
                                  }
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: isDark ? '#374151' : '#e5e7eb',
                                    '& .MuiLinearProgress-bar': {
                                      background: 'linear-gradient(90deg, #10b981, #34d399)',
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#10b981' }}>
                                    {(
                                      ((selectedTransaction.summary?.grandTotal - selectedTransaction.summary?.pendingAmount) /
                                        selectedTransaction.summary?.grandTotal) *
                                      100
                                    ).toFixed(1)}
                                    % Paid
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#ef4444' }}>
                                    {(
                                      (selectedTransaction.summary?.pendingAmount / selectedTransaction.summary?.grandTotal) *
                                      100
                                    ).toFixed(1)}
                                    % Pending
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Paper>
                      </Box>
                    </Grid>

                  </Grid>
                </Box>

                {/* Footer Actions */}
                {/* <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${alpha(
            isDark ? '#475569' : '#e2e8f0',
            isDark ? 0.2 : 0.3
          )}`,
          background: isDark
            ? 'rgba(15, 23, 42, 0.5)'
            : 'rgba(248, 250, 252, 0.5)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Button
          onClick={() => setOpenModal(false)}
          sx={{
            borderRadius: '12px',
            px: 4,
            color: isDark ? '#94a3b8' : '#64748b',
            border: `1px solid ${alpha(
              isDark ? '#475569' : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            '&:hover': {
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.02)',
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => handlePrintBill(selectedTransaction)}
          sx={{
            borderRadius: '12px',
            px: 4,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4)',
            },
          }}
        >
          Print Bill
        </Button>
      </DialogActions> */}
              </>
            )}
          </Dialog>
        </Container>
      </Box>
      <Dialog
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(30, 41, 59, 0.98))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98))',
            backdropFilter: 'blur(20px)',
            border: isDark
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: isDark
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#DC2626' }}>
              Cancel Bill #{selectedCancelTransaction?.transactionDetails?.billNo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
          <IconButton
            onClick={handleCancelModalClose}
            size="small"
            disabled={isCancelling}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          {selectedCancelTransaction && (
            <Grid container spacing={3}>
              {/* Bill Information */}
              <Grid item xs={12}>
                <Card sx={{
                  borderRadius: '12px',
                  background: 'rgba(220, 38, 38, 0.05)',
                  border: '1px solid rgba(220, 38, 38, 0.2)',
                }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#DC2626', mb: 1 }}>
                      Bill Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Bill No
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedCancelTransaction.transactionDetails?.billNo}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Original Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          ₹{selectedCancelTransaction.summary?.grandTotal?.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedCancelTransaction.date}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Paid Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#059669' }}>
                          ₹{selectedCancelTransaction.summary?.paymentDone?.toLocaleString('en-IN') || selectedCancelTransaction.summary?.grandTotal?.toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Refund Amount Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Refund Amount
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cash Refund (₹)"
                      value={refundAmounts.cash}
                      onChange={(e) => handleRefundChange('cash', e.target.value)}
                      disabled={isCancelling}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Bank Transfer (₹)"
                      value={refundAmounts.bank}
                      onChange={(e) => handleRefundChange('bank', e.target.value)}
                      disabled={isCancelling}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Card sx={{
                      mt: 2,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(34, 197, 94, 0.1))',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                            Total Refund
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#059669' }}>
                            ₹{refundAmounts.total.toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Items to be Updated */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Items to be Updated ({cancellingItems.length})
                </Typography>
                <Box sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  borderRadius: '12px',
                  border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  background: isDark
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.02)',
                  p: 2,
                }}>
                  {cancellingItems.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: '8px',
                        backgroundColor: isDark
                          ? 'rgba(255, 255, 255, 0.02)'
                          : 'rgba(0, 0, 0, 0.01)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            HUID: {item.huid} • Weight: {item.weight}g
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Serial Number
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.hasConflict ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ textDecoration: 'line-through', color: '#DC2626' }}>
                                  {item.originalSerialNo}
                                </span>
                                <ArrowForwardIcon sx={{ fontSize: '14px', color: '#6B7280' }} />
                                <span style={{ color: '#059669' }}>
                                  {item.updateData.serialNo}
                                </span>
                              </Box>
                            ) : (
                              item.originalSerialNo
                            )}
                          </Typography>
                          {item.hasConflict && (
                            <Typography variant="caption" color="#F59E0B" sx={{ display: 'block' }}>
                              Conflict resolved
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>

              {/* Silver Items to be Added Back to Stock */}
              {silverItemsArray.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#3B82F6' }}>
                    Silver Items to be Added ({silverItemsArray.length} type(s))
                  </Typography>
                  <Box sx={{
                    maxHeight: 150,
                    overflow: 'auto',
                    borderRadius: '12px',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    background: 'rgba(59, 130, 246, 0.05)',
                    p: 2,
                  }}>
                    {silverItemsArray.map((silverGroup, index) => (
                      <Box
                        key={silverGroup.silverType}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: '8px',
                          backgroundColor: 'rgba(59, 130, 246, 0.08)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1D4ED8' }}>
                              {silverGroup.silverType.toUpperCase()} Silver
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#3B82F6' }}>
                              {silverGroup.items.length} item(s) • Total Weight: {silverGroup.totalWeight.toFixed(2)}g
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ color: '#3B82F6', display: 'block' }}>
                              Will be added to stock
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Warning Message */}
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Important Notes:
                  </Typography>
                  <Typography variant="caption" component="div">
                    • This action will mark {cancellingItems.length} jewelry item(s) as available in stock
                    {silverItemsArray.length > 0 && (
                      <>
                        <br />
                        • {silverItemsArray.reduce((sum, group) => sum + group.items.length, 0)} silver item(s) ({silverItemsArray.length} type(s)) will be added back to silver stock
                      </>
                    )}
                    <br />
                    • Serial numbers will be updated if conflicts exist
                    <br />
                    • Refund amount should match the original payment
                    <br />
                    • A cancellation record will be created
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: isDark
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <Button
            onClick={handleCancelModalClose}
            disabled={isCancelling}
            sx={{
              color: '#6B7280',
              '&:hover': {
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancellation}
            disabled={isCancelling || refundAmounts.total <= 0}
            startIcon={isCancelling ? <CircularProgress size={16} /> : <CancelIcon />}
            sx={{
              background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #B91C1C, #991B1B)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.35)',
              },
              '&.Mui-disabled': {
                background: 'rgba(220, 38, 38, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );

  // Helper function
  function getCustomerName(customerId) {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'N/A';
  }
};

export default React.memo(TransactionHistory);